import { Modal } from 'antd';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  entityModes,
  serviceSetFilterFieldNames,
  serviceSetFilterSelectionValues,
} from '../CreateNewWorkScheduleByPeriod.constants';
import { useWorkScheduleServiceDetailsQuery } from '../../../api/useWorkScheduleServiceDetailsQuery';
import { useWorkScheduleServicesWithTreeQuery } from '../../../api/useWorkScheduleServicesWithTreeQuery';
import { getSelectedServiceTreeDetails } from '../../../model/workScheduleServiceTree';
import { ServiceSetsSectionView } from './ServiceSetsSection/ServiceSetsSectionView';
import {
  getArrayValue,
  getCreatePreviewServices,
  getFilteredServiceSetsByServices,
  getSelectionKey,
  getServiceOptionsFromServiceSets,
  getServiceReasonGroupsFromTree,
  getServiceSetOption,
  getVisibleServiceReasonGroups,
  hasSameSelection,
  serviceReasonSearchDebounceMs,
  sortSelectedFirst,
  toggleReasonSelection,
  toggleServiceSelection,
} from './ServiceSetsSection/utils';

export const ServiceSetsSection = forwardRef(function ServiceSetsSection(
  {
    form,
    isEditable = true,
    initialSelectedServiceSetIds = [],
    modeOptions,
    mode,
    isCreateMode,
    isEditMode,
    isDeleteMode,
    selectedServiceSetIds,
    selectedServiceSets = [],
    serviceSets = [],
    serviceSetOptions,
    serviceOptions,
    serviceReasonGroups,
    servicesLoading,
    serviceDetailsLoading,
    reasonsLoading,
    createLoading,
    updateLoading,
    deleteLoading,
    onChange,
    onCreate,
    onUpdate,
    onDelete,
    onModeChange,
  },
  ref
) {
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const formServiceSetName = getFieldValue('newServiceSetName') || '';
  const formServiceSetComment = getFieldValue('newServiceSetComment') || '';
  const formSelectedReasonIds = getArrayValue(
    getFieldValue('newServiceSetReasonIds')
  );
  const [serviceSetName, setServiceSetName] = useState(formServiceSetName);
  const [serviceSetComment, setServiceSetComment] = useState(
    formServiceSetComment
  );
  const [selectedReasonIds, setSelectedReasonIdsState] = useState(
    formSelectedReasonIds
  );
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [committedReasonIds, setCommittedReasonIds] = useState([]);
  const [committedServiceIds, setCommittedServiceIds] = useState([]);
  const [prioritizedServiceSetIds, setPrioritizedServiceSetIds] = useState(() =>
    getArrayValue(initialSelectedServiceSetIds)
  );
  const editHydrationKeyRef = useRef('');
  const [collapsedServiceKeys, setCollapsedServiceKeys] = useState(
    () => new Set()
  );
  const [editingServiceSetId, setEditingServiceSetId] = useState();
  const [deletingServiceSetId, setDeletingServiceSetId] = useState();
  const editingServiceSet = useMemo(
    () =>
      serviceSets.find(
        (serviceSet) => String(serviceSet.id) === String(editingServiceSetId)
      ),
    [editingServiceSetId, serviceSets]
  );
  const editingServiceSetComment = editingServiceSet?.comment || '';
  const editingServiceSetDetailsQuery = useWorkScheduleServicesWithTreeQuery(
    { workScheduleServiceNameId: editingServiceSetId },
    {
      enabled: isEditMode && Boolean(editingServiceSetId),
    }
  );
  const creatingServiceSetDetailsQuery = useWorkScheduleServicesWithTreeQuery(
    { workScheduleServiceNameId: undefined },
    {
      enabled: isCreateMode,
    }
  );
  const isEditingServiceSetReady =
    editingServiceSetDetailsQuery.isSuccess &&
    !editingServiceSetDetailsQuery.isFetching;
  const isCreatingServiceSetReady =
    creatingServiceSetDetailsQuery.isSuccess &&
    !creatingServiceSetDetailsQuery.isFetching;
  const editingServiceSetDetails = useMemo(
    () =>
      isEditingServiceSetReady
        ? getSelectedServiceTreeDetails(editingServiceSetDetailsQuery.data)
        : [],
    [editingServiceSetDetailsQuery.data, isEditingServiceSetReady]
  );
  const editorServiceReasonGroups = useMemo(() => {
    if (isEditMode) {
      return isEditingServiceSetReady
        ? getServiceReasonGroupsFromTree(
            editingServiceSetDetailsQuery.data,
            serviceReasonGroups
          )
        : [];
    }

    if (isCreateMode) {
      return isCreatingServiceSetReady
        ? getServiceReasonGroupsFromTree(
            creatingServiceSetDetailsQuery.data,
            serviceReasonGroups
          )
        : [];
    }

    return serviceReasonGroups;
  }, [
    creatingServiceSetDetailsQuery.data,
    editingServiceSetDetailsQuery.data,
    isCreateMode,
    isCreatingServiceSetReady,
    isEditingServiceSetReady,
    isEditMode,
    serviceReasonGroups,
  ]);
  const editingServiceSetWithDetails = useMemo(() => {
    if (!editingServiceSet) {
      return null;
    }

    return {
      ...editingServiceSet,
      services: editingServiceSetDetails,
    };
  }, [editingServiceSet, editingServiceSetDetails]);
  const editingServiceSetWithDetailsReasonIds = useMemo(
    () =>
      editorServiceReasonGroups.flatMap((service) =>
        (service.isSelected
          ? service.reasons
          : service.reasons.filter((reason) => reason.isSelected)
        ).map((reason) => reason.value)
      ),
    [editorServiceReasonGroups]
  );
  const editingServiceSetWithDetailsServiceIds = useMemo(
    () =>
      editorServiceReasonGroups
        .filter((service) => service.isSelected)
        .map((service) => service.value),
    [editorServiceReasonGroups]
  );
  const deletingServiceSet = useMemo(
    () =>
      serviceSets.find(
        (serviceSet) => String(serviceSet.id) === String(deletingServiceSetId)
      ),
    [deletingServiceSetId, serviceSets]
  );
  const deletingServiceSetDetailsQuery = useWorkScheduleServiceDetailsQuery(
    { workScheduleServiceNameId: deletingServiceSetId },
    {
      enabled: isDeleteMode && Boolean(deletingServiceSetId),
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );
  const deletingServiceSetDetails =
    deletingServiceSetDetailsQuery.isPreviousData
      ? []
      : deletingServiceSetDetailsQuery.data || [];
  const deletingServiceSetPreviewItems = useMemo(() => {
    if (!deletingServiceSet) {
      return [];
    }

    return [
      {
        ...deletingServiceSet,
        services: deletingServiceSetDetails.length
          ? deletingServiceSetDetails
          : deletingServiceSet.services || [],
      },
    ];
  }, [deletingServiceSet, deletingServiceSetDetails]);
  const isServiceSetEditorVisible =
    isCreateMode || Boolean(editingServiceSetWithDetails);
  const selectedReasonIdSet = useMemo(
    () => new Set(selectedReasonIds.map(String)),
    [selectedReasonIds]
  );
  const selectedServiceIdSet = useMemo(
    () => new Set(selectedServiceIds.map(String)),
    [selectedServiceIds]
  );
  const committedReasonIdSet = useMemo(
    () => new Set(committedReasonIds.map(String)),
    [committedReasonIds]
  );
  const committedServiceIdSet = useMemo(
    () => new Set(committedServiceIds.map(String)),
    [committedServiceIds]
  );
  const prioritizedServiceSetIdSet = useMemo(
    () => new Set(prioritizedServiceSetIds.map(String)),
    [prioritizedServiceSetIds]
  );
  const serviceFilterValue = getArrayValue(
    getFieldValue(serviceSetFilterFieldNames.service)
  );
  const immediateFilterValue = Boolean(
    getFieldValue(serviceSetFilterFieldNames.immediate)
  );
  const selectionFilterValue = getFieldValue(
    serviceSetFilterFieldNames.selection
  );
  const serviceFilterKey = getSelectionKey(serviceFilterValue);
  const serviceFilterKeys = useMemo(
    () => (serviceFilterKey ? serviceFilterKey.split('|') : []),
    [serviceFilterKey]
  );
  const selectedServiceSetIdsKey = getSelectionKey(selectedServiceSetIds);
  const selectedServiceSetValue = getArrayValue(selectedServiceSetIds)[0];
  const selectedServiceSetValues = useMemo(
    () =>
      selectedServiceSetValue !== undefined && selectedServiceSetValue !== null
        ? [selectedServiceSetValue]
        : [],
    [selectedServiceSetValue]
  );
  const selectedServiceSetValuesKey = useMemo(
    () => getSelectionKey(selectedServiceSetValues),
    [selectedServiceSetValues]
  );
  const selectedServiceSetIdSet = useMemo(
    () => new Set(selectedServiceSetValues.map(String)),
    [selectedServiceSetValues]
  );
  const hasSelectedServiceSets = Boolean(selectedServiceSetIdSet.size);
  const selectedSingleServiceSets = useMemo(
    () =>
      selectedServiceSets.filter((serviceSet) =>
        selectedServiceSetIdSet.has(String(serviceSet.id))
      ),
    [selectedServiceSetIdSet, selectedServiceSets]
  );
  const selectedServiceFilterOptions = useMemo(
    () => getServiceOptionsFromServiceSets(selectedSingleServiceSets),
    [selectedSingleServiceSets]
  );
  const sortedServiceSetOptions = useMemo(
    () =>
      sortSelectedFirst(serviceSets, (serviceSet) =>
        prioritizedServiceSetIdSet.has(String(serviceSet.id))
      ).map(getServiceSetOption),
    [prioritizedServiceSetIdSet, serviceSets]
  );
  const filteredSelectedServiceSets = useMemo(
    () =>
      getFilteredServiceSetsByServices({
        serviceSets: selectedSingleServiceSets,
        serviceFilterValues: serviceFilterKeys,
        showImmediateOnly:
          selectionFilterValue === serviceSetFilterSelectionValues.immediate,
      }),
    [selectedSingleServiceSets, selectionFilterValue, serviceFilterKeys]
  );
  const filteredServiceReasonGroups = useMemo(() => {
    return getVisibleServiceReasonGroups({
      debouncedSearchValue,
      committedReasonIdSet,
      committedServiceIdSet,
      immediateFilterValue,
      isServiceSetEditorVisible,
      selectedReasonIdSet,
      selectedServiceIdSet,
      selectionFilterValue,
      serviceFilterKeys,
      serviceReasonGroups: editorServiceReasonGroups,
      serviceSetFilterSelectionValues,
    });
  }, [
    debouncedSearchValue,
    committedReasonIdSet,
    committedServiceIdSet,
    immediateFilterValue,
    isServiceSetEditorVisible,
    selectedReasonIdSet,
    selectedServiceIdSet,
    selectionFilterValue,
    serviceFilterKeys,
    editorServiceReasonGroups,
  ]);
  const visibleReasonIds = useMemo(
    () =>
      filteredServiceReasonGroups.flatMap((service) =>
        service.visibleReasons.map((reason) => reason.value)
      ),
    [filteredServiceReasonGroups]
  );
  const hasFilters = Boolean(
    serviceFilterValue.length || immediateFilterValue || selectionFilterValue
  );
  const hasVisibleItems = Boolean(filteredServiceReasonGroups.length);
  const hasVisibleUnselectedReasons = useMemo(
    () =>
      visibleReasonIds.some(
        (reasonId) => !selectedReasonIdSet.has(String(reasonId))
      ) ||
      Boolean(
        selectedServiceIdSet &&
          filteredServiceReasonGroups.some(
            (service) => !selectedServiceIdSet.has(String(service.value))
          )
      ),
    [
      filteredServiceReasonGroups,
      selectedReasonIdSet,
      selectedServiceIdSet,
      visibleReasonIds,
    ]
  );
  const hasVisibleSelectedReasons = useMemo(
    () =>
      visibleReasonIds.some((reasonId) =>
        selectedReasonIdSet.has(String(reasonId))
      ) ||
      Boolean(
        selectedServiceIdSet &&
          filteredServiceReasonGroups.some((service) =>
            selectedServiceIdSet.has(String(service.value))
          )
      ),
    [
      filteredServiceReasonGroups,
      selectedReasonIdSet,
      selectedServiceIdSet,
      visibleReasonIds,
    ]
  );
  const previewServices = useMemo(
    () =>
      getCreatePreviewServices({
        selectedReasonIds,
        selectedServiceIdSet,
        serviceReasonGroups: editorServiceReasonGroups,
      }),
    [selectedReasonIds, selectedServiceIdSet, editorServiceReasonGroups]
  );
  const submitDisabledReason = useMemo(() => {
    if (createLoading || updateLoading) {
      return 'Набор услуг сохраняется';
    }

    if (!isEditable) {
      return 'Редактирование набора услуг недоступно';
    }

    if (isEditMode && !editingServiceSet) {
      return 'Выберите набор услуг для изменения';
    }

    if (isEditMode && !isEditingServiceSetReady) {
      return editingServiceSetDetailsQuery.isError
        ? 'Не удалось загрузить услуги и причины набора'
        : 'Загрузка услуг и причин набора';
    }

    if (isCreateMode && !isCreatingServiceSetReady) {
      return creatingServiceSetDetailsQuery.isError
        ? 'Не удалось загрузить услуги и причины'
        : 'Загрузка услуг и причин';
    }

    if (!serviceSetName.trim()) {
      return 'Введите наименование набора услуг';
    }

    if (!selectedReasonIds.length && !selectedServiceIds.length) {
      return 'Выберите минимум одну услугу или причину';
    }

    return '';
  }, [
    createLoading,
    creatingServiceSetDetailsQuery.isError,
    editingServiceSet,
    editingServiceSetDetailsQuery.isError,
    isCreateMode,
    isCreatingServiceSetReady,
    isEditingServiceSetReady,
    isEditable,
    isEditMode,
    selectedReasonIds.length,
    selectedServiceIds.length,
    serviceSetName,
    updateLoading,
  ]);
  const isSubmitDisabled = Boolean(submitDisabledReason);
  const isEditServiceSetDirty = Boolean(
    isEditMode &&
      isEditingServiceSetReady &&
      editingServiceSetWithDetails &&
      ((serviceSetName || '').trim() !==
        (editingServiceSetWithDetails.name || '').trim() ||
        serviceSetComment.trim() !== editingServiceSetComment.trim() ||
        !hasSameSelection(
          selectedServiceIds,
          editingServiceSetWithDetailsServiceIds
        ) ||
        !hasSameSelection(
          selectedReasonIds,
          editingServiceSetWithDetailsReasonIds
        ))
  );
  const submitButtonDisabled = Boolean(
    isSubmitDisabled || (isEditMode && !isEditServiceSetDirty)
  );
  const submitButtonTooltip =
    submitDisabledReason ||
    (isEditMode
      ? isEditServiceSetDirty
        ? 'Есть несохраненные изменения'
        : 'Нет изменений'
      : '');

  const handleEditingServiceSetChange = useCallback(
    (serviceSetId) => {
      const nextEditingServiceSet = serviceSets.find(
        (serviceSet) => String(serviceSet.id) === String(serviceSetId)
      );
      const nextReasonIds = [];
      const nextName = nextEditingServiceSet?.name || '';
      const nextComment = nextEditingServiceSet?.comment || '';

      setEditingServiceSetId(serviceSetId);
      editHydrationKeyRef.current = '';
      setServiceSetName(nextName);
      setServiceSetComment(nextComment);
      setSelectedReasonIdsState(nextReasonIds);
      setSelectedServiceIds([]);
      setCommittedReasonIds([]);
      setCommittedServiceIds([]);
      setCollapsedServiceKeys(new Set());
      setFieldsValue({
        newServiceSetName: nextName,
        newServiceSetComment: nextComment,
        newServiceSetReasonIds: nextReasonIds,
      });
    },
    [serviceSets, setFieldsValue]
  );

  const resetServiceSetEditor = useCallback(() => {
    setServiceSetName('');
    setServiceSetComment('');
    setSelectedReasonIdsState([]);
    setSelectedServiceIds([]);
    setCommittedReasonIds([]);
    setCommittedServiceIds([]);
    setCollapsedServiceKeys(new Set());
    setEditingServiceSetId(undefined);
    setDeletingServiceSetId(undefined);
    editHydrationKeyRef.current = '';
    setSearchValue('');
    setDebouncedSearchValue('');
    setFieldsValue({
      newServiceSetName: '',
      newServiceSetComment: '',
      newServiceSetReasonIds: [],
      [serviceSetFilterFieldNames.immediate]: false,
      [serviceSetFilterFieldNames.service]: [],
      [serviceSetFilterFieldNames.selection]: '',
    });
  }, [setFieldsValue]);

  const handleModeChange = useCallback(
    (nextMode) => {
      if (nextMode === mode) {
        return;
      }

      resetServiceSetEditor();

      if (
        nextMode === entityModes.edit &&
        selectedServiceSetValue !== undefined &&
        selectedServiceSetValue !== null
      ) {
        handleEditingServiceSetChange(selectedServiceSetValue);
      }

      onModeChange(nextMode);
    },
    [
      handleEditingServiceSetChange,
      mode,
      onModeChange,
      resetServiceSetEditor,
      selectedServiceSetValue,
    ]
  );

  useEffect(() => {
    if (!isCreateMode && !isEditMode && !isDeleteMode) {
      resetServiceSetEditor();
    }
  }, [isCreateMode, isDeleteMode, isEditMode, resetServiceSetEditor]);

  useEffect(() => {
    if (isCreateMode) {
      resetServiceSetEditor();
    }
  }, [isCreateMode, resetServiceSetEditor]);

  useEffect(() => {
    if (
      getSelectionKey(getFieldValue('serviceSetIds')) !==
      selectedServiceSetValuesKey
    ) {
      setFieldsValue({ serviceSetIds: selectedServiceSetValues });
    }

    if (selectedServiceSetIdsKey !== selectedServiceSetValuesKey) {
      onChange(selectedServiceSetValues);
    }
  }, [
    getFieldValue,
    onChange,
    selectedServiceSetIdsKey,
    selectedServiceSetValues,
    selectedServiceSetValuesKey,
    setFieldsValue,
  ]);

  useEffect(() => {
    if (isCreateMode || isEditMode || !serviceFilterValue.length) {
      return;
    }

    const availableServiceValueSet = new Set(
      selectedServiceFilterOptions.map((option) => String(option.value))
    );
    const validServiceFilterValue = serviceFilterValue.filter((serviceId) =>
      availableServiceValueSet.has(String(serviceId))
    );

    if (getSelectionKey(validServiceFilterValue) !== serviceFilterKey) {
      setFieldsValue({
        [serviceSetFilterFieldNames.service]: validServiceFilterValue,
      });
    }
  }, [
    isCreateMode,
    isEditMode,
    selectedServiceFilterOptions,
    serviceFilterKey,
    serviceFilterValue,
    setFieldsValue,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, serviceReasonSearchDebounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const handleServiceSetNameChange = useCallback((event) => {
    setServiceSetName(event.target.value);
  }, []);

  const handleServiceSetNameBlur = useCallback(() => {
    if (getFieldValue('newServiceSetName') === serviceSetName) {
      return;
    }

    setFieldsValue({ newServiceSetName: serviceSetName });
  }, [getFieldValue, serviceSetName, setFieldsValue]);

  const handleServiceSetCommentChange = useCallback((event) => {
    setServiceSetComment(event.target.value);
  }, []);

  const handleServiceSetCommentBlur = useCallback(() => {
    if (getFieldValue('newServiceSetComment') === serviceSetComment) {
      return;
    }

    setFieldsValue({ newServiceSetComment: serviceSetComment });
  }, [getFieldValue, serviceSetComment, setFieldsValue]);

  const handleServiceSetsChange = useCallback(
    (value) => {
      const nextValue = getArrayValue(value);

      setFieldsValue({ serviceSetIds: nextValue });
      onChange(nextValue);
    },
    [onChange, setFieldsValue]
  );

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    setServiceSetComment(editingServiceSetComment);
    setFieldsValue({ newServiceSetComment: editingServiceSetComment });
  }, [
    editingServiceSetComment,
    editingServiceSetId,
    isEditMode,
    setFieldsValue,
  ]);

  useEffect(() => {
    if (
      !isEditMode ||
      !editingServiceSetWithDetails ||
      !isEditingServiceSetReady
    ) {
      return;
    }

    const nextReasonIds = editingServiceSetWithDetailsReasonIds;
    const nextHydrationKey = String(editingServiceSetId);

    if (editHydrationKeyRef.current === nextHydrationKey) {
      return;
    }

    editHydrationKeyRef.current = nextHydrationKey;
    setSelectedReasonIdsState(nextReasonIds);
    setSelectedServiceIds(editingServiceSetWithDetailsServiceIds);
    setCommittedReasonIds(nextReasonIds);
    setCommittedServiceIds(editingServiceSetWithDetailsServiceIds);
    setFieldsValue({
      newServiceSetReasonIds: nextReasonIds,
    });
  }, [
    isEditingServiceSetReady,
    editingServiceSetId,
    editingServiceSetWithDetails,
    editingServiceSetWithDetailsReasonIds,
    editingServiceSetWithDetailsServiceIds,
    isEditMode,
    setFieldsValue,
  ]);

  const handleDeletingServiceSetChange = useCallback((serviceSetId) => {
    setDeletingServiceSetId(serviceSetId);
  }, []);

  const setSelectedReasonIds = useCallback((nextReasonIds) => {
    setSelectedReasonIdsState(nextReasonIds);
  }, []);

  const handleReasonClick = useCallback(
    (reasonId) => {
      if (!isEditable) {
        return;
      }

      const nextSelection = toggleReasonSelection({
        reasonId,
        selectedReasonIds,
        selectedServiceIds,
        serviceReasonGroups: editorServiceReasonGroups,
      });

      setSelectedReasonIdsState(nextSelection.selectedReasonIds);
      setSelectedServiceIds(nextSelection.selectedServiceIds);
    },
    [
      editorServiceReasonGroups,
      isEditable,
      selectedReasonIds,
      selectedServiceIds,
    ]
  );

  const handleServiceClick = useCallback(
    (service) => {
      if (!isEditable) {
        return;
      }

      const nextSelection = toggleServiceSelection({
        selectedReasonIds,
        selectedServiceIds,
        service,
      });

      setSelectedReasonIdsState(nextSelection.selectedReasonIds);
      setSelectedServiceIds(nextSelection.selectedServiceIds);
    },
    [isEditable, selectedReasonIds, selectedServiceIds]
  );

  const handleServiceCollapseToggle = useCallback((serviceKey) => {
    setCollapsedServiceKeys((prevServiceKeys) => {
      const nextServiceKeys = new Set(prevServiceKeys);
      const normalizedServiceKey = String(serviceKey);

      if (nextServiceKeys.has(normalizedServiceKey)) {
        nextServiceKeys.delete(normalizedServiceKey);
      } else {
        nextServiceKeys.add(normalizedServiceKey);
      }

      return nextServiceKeys;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!isEditable || !hasVisibleUnselectedReasons) {
      return;
    }

    const selectedReasonKeys = new Set(selectedReasonIds.map(String));
    const nextReasonIds = [...selectedReasonIds];

    visibleReasonIds.forEach((reasonId) => {
      if (!selectedReasonKeys.has(String(reasonId))) {
        selectedReasonKeys.add(String(reasonId));
        nextReasonIds.push(reasonId);
      }
    });
    const selectedServiceKeys = new Set(selectedServiceIds.map(String));
    const nextServiceIds = [...selectedServiceIds];

    editorServiceReasonGroups.forEach((service) => {
      const allReasonsAreSelected = Boolean(
        service.reasonKeys.length &&
          service.reasonKeys.every((reasonId) =>
            selectedReasonKeys.has(String(reasonId))
          )
      );

      if (
        allReasonsAreSelected &&
        !selectedServiceKeys.has(String(service.value))
      ) {
        selectedServiceKeys.add(String(service.value));
        nextServiceIds.push(service.value);
      }
    });
    filteredServiceReasonGroups
      .filter((service) => !service.reasonKeys.length)
      .forEach((service) => {
        if (!selectedServiceKeys.has(String(service.value))) {
          selectedServiceKeys.add(String(service.value));
          nextServiceIds.push(service.value);
        }
      });

    setSelectedReasonIdsState(nextReasonIds);
    setSelectedServiceIds(nextServiceIds);
  }, [
    editorServiceReasonGroups,
    filteredServiceReasonGroups,
    hasVisibleUnselectedReasons,
    isEditable,
    selectedReasonIds,
    selectedServiceIds,
    visibleReasonIds,
  ]);

  const handleDeselectAll = useCallback(() => {
    if (!isEditable || !hasVisibleSelectedReasons) {
      return;
    }

    let nextSelection = {
      selectedReasonIds,
      selectedServiceIds,
    };

    visibleReasonIds.forEach((reasonId) => {
      if (
        nextSelection.selectedReasonIds.some(
          (selectedReasonId) => String(selectedReasonId) === String(reasonId)
        )
      ) {
        nextSelection = toggleReasonSelection({
          reasonId,
          ...nextSelection,
          serviceReasonGroups: editorServiceReasonGroups,
        });
      }
    });
    filteredServiceReasonGroups
      .filter((service) => !service.visibleReasons.length)
      .forEach((service) => {
        if (
          nextSelection.selectedServiceIds.some(
            (serviceId) => String(serviceId) === String(service.value)
          )
        ) {
          nextSelection = toggleServiceSelection({
            ...nextSelection,
            service,
          });
        }
      });

    setSelectedReasonIdsState(nextSelection.selectedReasonIds);
    setSelectedServiceIds(nextSelection.selectedServiceIds);
  }, [
    editorServiceReasonGroups,
    filteredServiceReasonGroups,
    hasVisibleSelectedReasons,
    isEditable,
    selectedReasonIds,
    selectedServiceIds,
    visibleReasonIds,
  ]);

  const commitServiceSetChanges = useCallback(async () => {
    if (!isEditable) {
      Modal.warning({
        title: 'Действие недоступно',
        content: 'Редактирование набора услуг недоступно',
      });
      return { success: false };
    }

    if (isEditMode && !editingServiceSet) {
      Modal.warning({
        title: 'Заполните обязательные данные',
        content: 'Выберите набор услуг для изменения',
      });
      return { success: false };
    }

    if (isEditMode && !isEditingServiceSetReady) {
      Modal.warning({
        title: 'Услуги и причины не загружены',
        content: submitDisabledReason,
      });
      return { success: false };
    }

    if (!serviceSetName.trim()) {
      Modal.warning({
        title: 'Заполните обязательные данные',
        content: 'Введите наименование набора услуг',
      });
      return { success: false };
    }

    if (!selectedReasonIds.length && !selectedServiceIds.length) {
      Modal.warning({
        title: 'Заполните обязательные данные',
        content: 'Выберите минимум одну услугу или причину',
      });
      return { success: false };
    }

    setFieldsValue({
      newServiceSetName: serviceSetName,
      newServiceSetComment: serviceSetComment,
      newServiceSetReasonIds: selectedReasonIds,
    });

    const payload = {
      serviceSetName,
      serviceSetComment,
      reasonIds: selectedReasonIds,
      serviceIds: selectedServiceIds,
    };
    const savedServiceSet = isEditMode
      ? await onUpdate(editingServiceSet.id, {
          ...payload,
          serviceReasonGroups: editorServiceReasonGroups,
        })
      : await onCreate({
          ...payload,
          serviceReasonGroups: editorServiceReasonGroups,
        });

    if (!savedServiceSet) {
      return { success: false };
    }

    if (savedServiceSet?.id !== undefined && savedServiceSet?.id !== null) {
      const nextServiceSetIds = [savedServiceSet.id];

      setPrioritizedServiceSetIds(nextServiceSetIds);
      setFieldsValue({ serviceSetIds: nextServiceSetIds });
      onChange(nextServiceSetIds);
    }

    setServiceSetName('');
    setServiceSetComment('');
    setSelectedReasonIds([]);
    setSelectedServiceIds([]);
    setEditingServiceSetId(undefined);
    return { success: true, serviceSet: savedServiceSet };
  }, [
    editingServiceSet,
    editorServiceReasonGroups,
    isEditable,
    isEditingServiceSetReady,
    isEditMode,
    onCreate,
    onChange,
    onUpdate,
    selectedReasonIds,
    selectedServiceIds,
    serviceSetComment,
    serviceSetName,
    setFieldsValue,
    setSelectedReasonIds,
    submitDisabledReason,
  ]);

  const handleSubmit = useCallback(() => {
    commitServiceSetChanges().catch(() => null);
  }, [commitServiceSetChanges]);

  const handleDeleteSubmit = useCallback(() => {
    if (!deletingServiceSet) {
      Modal.warning({
        title: 'Заполните обязательные данные',
        content: 'Выберите набор услуг для удаления',
      });
      return;
    }

    Modal.confirm({
      title: 'Удалить набор услуг?',
      content: 'Набор услуг будет удален из списка доступных наборов.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        const result = await onDelete?.(deletingServiceSet.id);

        if (!result?.success) {
          return;
        }

        setDeletingServiceSetId(undefined);
      },
    });
  }, [deletingServiceSet, onDelete]);

  useImperativeHandle(
    ref,
    () => ({
      commitPendingChanges: () => {
        if (isEditMode && editingServiceSet && !isEditingServiceSetReady) {
          return commitServiceSetChanges();
        }

        if (!isEditMode || !isEditServiceSetDirty) {
          return { success: true };
        }

        return commitServiceSetChanges();
      },
    }),
    [
      commitServiceSetChanges,
      editingServiceSet,
      isEditingServiceSetReady,
      isEditMode,
      isEditServiceSetDirty,
    ]
  );

  const viewProps = {
    collapsedServiceKeys,
    editingServiceSet,
    editingServiceSetId,
    filteredSelectedServiceSets,
    filteredServiceReasonGroups,
    form,
    getFieldDecorator,
    deletingServiceSetId,
    deletingServiceSetPreviewItems,
    hasFilters,
    hasSelectedServiceSets,
    hasVisibleItems,
    hasVisibleSelectedReasons,
    hasVisibleUnselectedReasons,
    isCreateMode,
    isDeleteMode,
    isEditMode,
    isEditServiceSetDirty,
    isEditable,
    isSubmitDisabled,
    mode,
    modeOptions,
    previewServices,
    createLoading,
    updateLoading,
    deleteLoading,
    deletingServiceSetDetailsLoading: deletingServiceSetDetailsQuery.isFetching,
    editingServiceSetDetailsLoading: editingServiceSetDetailsQuery.isFetching,
    reasonsLoading:
      reasonsLoading ||
      (isCreateMode && creatingServiceSetDetailsQuery.isFetching),
    searchValue,
    selectedReasonIdSet,
    selectedServiceIdSet,
    selectedServiceFilterOptions,
    selectedServiceSetValue,
    selectedServiceSetValues,
    serviceOptions:
      isCreateMode || isEditMode ? editorServiceReasonGroups : serviceOptions,
    serviceSetComment,
    serviceSetName,
    serviceSetOptions,
    serviceDetailsLoading,
    servicesLoading,
    sortedServiceSetOptions,
    submitButtonDisabled,
    submitButtonTooltip,
    submitDisabledReason,
    onDeselectAll: handleDeselectAll,
    onDeletingServiceSetChange: handleDeletingServiceSetChange,
    onDeleteSubmit: handleDeleteSubmit,
    onEditingServiceSetChange: handleEditingServiceSetChange,
    onModeChange: handleModeChange,
    onCancel: () => handleModeChange('select'),
    onReasonClick: handleReasonClick,
    onSearchChange: (event) => setSearchValue(event.target.value),
    onSelectAll: handleSelectAll,
    onServiceClick: handleServiceClick,
    onServiceCollapseToggle: handleServiceCollapseToggle,
    onServiceSetCommentBlur: handleServiceSetCommentBlur,
    onServiceSetCommentChange: handleServiceSetCommentChange,
    onServiceSetNameBlur: handleServiceSetNameBlur,
    onServiceSetNameChange: handleServiceSetNameChange,
    onServiceSetsChange: handleServiceSetsChange,
    onSubmit: handleSubmit,
  };

  return <ServiceSetsSectionView {...viewProps} />;
});
